import PropTypes from 'prop-types';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import { ATTENDANCE_STATUS_META, attendancePercentageColor } from '../utils/session-labels';

const styles = {
  card: { p: 2.5, borderRadius: 2, border: `1px solid ${colors.GRAY_HR}`, boxShadow: 'none' },
  label: {
    fontSize: 11.5,
    color: colors.GRAY_DARK,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    mb: 1.5,
  },
  pct: { display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1.5 },
  bar: { height: 8, bgcolor: colors.BLUE_GRAY, borderRadius: 99, overflow: 'hidden', display: 'flex' },
  legendRow: { display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, fontSize: 12.5 },
  dot: bg => ({ width: 10, height: 10, borderRadius: 0.5, bgcolor: bg }),
};

const EMPTY_STATS = {
  attendancePercentage: 0,
  totalExpected: 0,
  counters: { present: 0, justified: 0, absent: 0, pending: 0 },
};

/**
 * Pure presentational — renders the precomputed stats from
 * `GET /api/sessions/:id/stats`. No aggregation, no filtering. Receives `null`
 * while the backend response is in flight.
 */
export default function SessionSummaryCard({ stats, loading }) {
  const { t2 } = useTranslation('EditCourseSessions');

  if (!stats && loading) {
    return (
      <Card sx={styles.card} elevation={0}>
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="80%" height={48} />
        <Skeleton variant="rectangular" height={8} sx={{ mt: 2, borderRadius: 99 }} />
      </Card>
    );
  }

  const s = stats || EMPTY_STATS;
  const counters = s.counters || EMPTY_STATS.counters;
  const totalForBar = Math.max(1, s.totalExpected || 0);

  return (
    <Card sx={styles.card} elevation={0}>
      <Typography sx={styles.label}>{t2('detail.attendanceSection')}</Typography>
      <Box sx={styles.pct}>
        <Typography
          sx={{
            fontSize: 36,
            fontWeight: 700,
            color: attendancePercentageColor(s.attendancePercentage),
            lineHeight: 1,
          }}
        >
          {s.attendancePercentage}%
        </Typography>
      </Box>
      <Box sx={styles.bar}>
        <Box
          sx={{ width: `${(counters.present / totalForBar) * 100}%`, bgcolor: ATTENDANCE_STATUS_META.present.color }}
        />
        <Box
          sx={{
            width: `${(counters.justified / totalForBar) * 100}%`,
            bgcolor: ATTENDANCE_STATUS_META.justified.color,
          }}
        />
        <Box
          sx={{ width: `${(counters.absent / totalForBar) * 100}%`, bgcolor: ATTENDANCE_STATUS_META.absent.color }}
        />
      </Box>
      {[
        ['present', counters.present],
        ['justified', counters.justified],
        ['absent', counters.absent],
        ['pending', counters.pending],
      ].map(([key, count]) => {
        const meta = ATTENDANCE_STATUS_META[key];
        return (
          <Box key={key} sx={styles.legendRow}>
            <Box sx={styles.dot(meta.color)} />
            <Typography sx={{ fontSize: 12.5, color: colors.GRAY_DARKER, flex: 1 }}>{meta.label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.BLACK }}>{count}</Typography>
          </Box>
        );
      })}
    </Card>
  );
}

SessionSummaryCard.propTypes = {
  stats: PropTypes.shape({
    attendancePercentage: PropTypes.number,
    totalExpected: PropTypes.number,
    counters: PropTypes.shape({
      present: PropTypes.number,
      justified: PropTypes.number,
      absent: PropTypes.number,
      pending: PropTypes.number,
    }),
  }),
  loading: PropTypes.bool,
};

SessionSummaryCard.defaultProps = { stats: null, loading: false };
